(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.CjsFulfilment = api;
})(typeof globalThis === 'object' ? globalThis : this, function () {
  const propertyName = 'Dispatch estimate';
  const units = (value) => Number.isSafeInteger(value) ? Math.max(0, value) : null;

  function isManaged(variant) {
    if (variant?.managed === true) return true;
    const data = variant?.fulfilment;
    return data?.version === 1 && data.enabled === true && Array.isArray(data.suppliers) &&
      data.suppliers.some(supplier => String(supplier.locationId) === '68185718917' &&
        (!supplier.name || supplier.name === 'Motorstate') && (!data.supplierName || data.supplierName === 'Motorstate'));
  }

  function resolve(variant, quantity) {
    if (!isManaged(variant)) return { state: 'unmanaged' };
    const data = variant.fulfilment;
    const requested = units(quantity);
    if (!requested || !data || data.version !== 1 || data.enabled !== true ||
        data.inventoryQuantity !== variant.inventoryQuantity || !Array.isArray(data.suppliers)) {
      return { state: 'unknown' };
    }
    const total = units(variant.inventoryQuantity);
    if (total === null) return { state: 'unknown' };
    if (requested > total) return { state: 'unavailable' };
    const workshop = units(data.workshop?.available);
    if (workshop === null) return { state: 'unknown' };
    if (requested <= workshop) return { state: 'workshop', minBusinessDays: 0, maxBusinessDays: 0 };
    let remaining = requested - workshop;
    let minBusinessDays = 0;
    let maxBusinessDays = 0;
    for (const supplier of data.suppliers) {
      const available = units(supplier.available);
      if (available === null) return { state: 'unknown' };
      if (!available) continue;
      const minimum = units(supplier.minBusinessDays);
      const maximum = units(supplier.maxBusinessDays);
      if (!minimum || maximum === null || maximum < minimum) return { state: 'unknown' };
      remaining -= Math.min(remaining, available);
      minBusinessDays = Math.max(minBusinessDays, minimum);
      maxBusinessDays = Math.max(maxBusinessDays, maximum);
      if (!remaining) return { state: 'supplier', minBusinessDays, maxBusinessDays };
    }
    return { state: 'unavailable' };
  }

  function variantQuantities(items) {
    const totals = new Map();
    for (const item of items || []) {
      const id = String(item.variantId);
      totals.set(id, (totals.get(id) || 0) + (units(item.quantity) || 0));
    }
    return totals;
  }

  function cartState(items, decorate = state => state) {
    const totals = variantQuantities(items);
    const seen = new Set();
    const states = [];
    for (const item of items || []) {
      const id = String(item.variantId);
      if (seen.has(id) || !isManaged(item.variant)) continue;
      seen.add(id);
      states.push(decorate(resolve(item.variant, totals.get(id)), item.variant));
    }
    if (!states.length) return { state: 'unmanaged' };
    if (states.some(state => state.state === 'unknown')) return { state: 'unknown' };
    if (states.some(state => state.state === 'unavailable')) return { state: 'unavailable' };
    const delayed = states.filter(state => state.state === 'supplier');
    if (!delayed.length) return { state: 'workshop', minBusinessDays: 0, maxBusinessDays: 0 };
    const result = { state: 'supplier',
      minBusinessDays: Math.max(...delayed.map(state => state.minBusinessDays)),
      maxBusinessDays: Math.max(...delayed.map(state => state.maxBusinessDays)) };
    // A concrete date is safe only when every delayed item has a supported calendar.
    if (delayed.every(state => state.dispatchDate)) {
      const latest = delayed.reduce((a, b) => a.dispatchDate >= b.dispatchDate ? a : b);
      Object.assign(result, latest, { minBusinessDays: result.minBusinessDays, maxBusinessDays: result.maxBusinessDays });
    }
    return result;
  }

  function dispatchMessage(state, messages) {
    if (state.state === 'supplier') {
      return messages.supplier_dispatch.replace('[min]', state.minBusinessDays).replace('[max]', state.maxBusinessDays);
    }
    return messages[state.state + '_dispatch'] || messages.unknown_dispatch;
  }

  function stockLabel(variant, state, messages) {
    const label = messages[state.state + '_label'] || messages.unknown_label;
    if (state.state !== 'supplier') return label;
    const data = variant?.fulfilment;
    const total = units(variant?.inventoryQuantity);
    const workshop = units(data?.workshop?.available);
    if (!data || data.inventoryQuantity !== variant.inventoryQuantity || total === null ||
        workshop === null || !Array.isArray(data.suppliers)) return label;
    // Native sellable stock caps the supplier count. These are customer sale
    // units (including quantity variants), never raw component/supplier units.
    const cap = Math.max(0, total - workshop);
    let supplierAvailable = 0;
    for (const supplier of data.suppliers) {
      const available = units(supplier?.available);
      if (available === null) return label;
      supplierAvailable = Math.min(cap, supplierAvailable + Math.min(cap, available));
    }
    if (!supplierAvailable) return label;
    return `${supplierAvailable >= 100 ? '100+' : supplierAvailable} ${label}`;
  }

  return { isManaged, propertyName, resolve, variantQuantities, cartState, dispatchMessage, stockLabel };
});
